import type { CSSProperties } from "react";
import { API_BASE_URL } from "../services/http";
import {
  DEFAULT_BRAND_DEEP_HEX,
  DEFAULT_BRAND_DEEPEST_HEX,
  DEFAULT_BRAND_HEX,
} from "./brandScale";

/**
 * Cabecalho de quem nao escolheu cor. Era um marrom sem relacao com o produto;
 * agora e o violeta da marca, o mesmo que a paleta usa por padrao.
 */
const DEFAULT_BANNER_GRADIENT = `linear-gradient(120deg, ${DEFAULT_BRAND_HEX} 0%, ${DEFAULT_BRAND_DEEP_HEX} 44%, ${DEFAULT_BRAND_DEEPEST_HEX} 100%)`;

export type EstablishmentThemeSource = {
  backgroundColor?: string | null;
  backgroundImageUrl?: string | null;
  headerTitleColor?: string | null;
  headerDescriptionColor?: string | null;
  infoBoxBackgroundColor?: string | null;
  infoBoxTitleColor?: string | null;
  infoBoxTextColor?: string | null;
  infoBoxOpacity?: number | null;
  infoBoxBlur?: number | null;
  bannerOverlayOpacity?: number | null;
};

export function resolveThemeImageUrl(url?: string | null): string {
  const normalized = String(url || "").trim();
  if (!normalized) return "";
  return normalized.startsWith("/") ? `${API_BASE_URL}${normalized}` : normalized;
}

export function hexToRgba(hex?: string | null, alpha = 0.45): string | null {
  if (!hex) return null;
  let h = String(hex).trim();
  if (!h.startsWith("#")) return null;
  if (h.length === 4) {
    h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }
  const match = /^#([0-9a-fA-F]{6})$/.exec(h);
  if (!match) return null;
  const numeric = parseInt(match[1], 16);
  const r = (numeric >> 16) & 255;
  const g = (numeric >> 8) & 255;
  const b = numeric & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function clampThemeNumber(
  value: unknown,
  min: number,
  max: number,
  fallback: number
): number {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return fallback;
  return Math.min(max, Math.max(min, numberValue));
}

export function buildEstablishmentBannerStyle(
  establishment: EstablishmentThemeSource | null | undefined
): CSSProperties {
  if (!establishment) {
    return { background: DEFAULT_BANNER_GRADIENT };
  }

  const imageUrl = resolveThemeImageUrl(establishment.backgroundImageUrl);
  const hasImage = Boolean(imageUrl);
  const hasColor = Boolean(establishment.backgroundColor);

  if (!hasImage && !hasColor) {
    return { background: DEFAULT_BANNER_GRADIENT };
  }

  if (hasImage && !hasColor) {
    return {
      backgroundImage: `linear-gradient(120deg, ${hexToRgba(DEFAULT_BRAND_HEX, 0.76)}, ${hexToRgba(DEFAULT_BRAND_DEEPEST_HEX, 0.68)}), url(${imageUrl})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    };
  }

  if (!hasImage && hasColor) {
    const baseColor = establishment.backgroundColor || DEFAULT_BRAND_HEX;
    return {
      background: `linear-gradient(120deg, ${baseColor} 0%, ${baseColor} 100%)`,
    };
  }

  const overlay = hexToRgba(establishment.backgroundColor, 0.66);
  const overlayEnd = hexToRgba(establishment.backgroundColor, 0.52);
  return {
    backgroundImage: `linear-gradient(120deg, ${overlay || hexToRgba(DEFAULT_BRAND_HEX, 0.66)}, ${overlayEnd || hexToRgba(DEFAULT_BRAND_DEEPEST_HEX, 0.62)}), url(${imageUrl})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
}

export function buildEstablishmentThemeTokens(
  establishment: EstablishmentThemeSource | null | undefined
) {
  const baseColor = establishment?.backgroundColor || DEFAULT_BRAND_HEX;
  const titleColor = establishment?.headerTitleColor || "#ffffff";
  const descriptionColor = establishment?.headerDescriptionColor || "#f8fafc";
  const addressColor = establishment?.infoBoxTextColor || descriptionColor;
  const cardBackgroundColor = establishment?.infoBoxBackgroundColor || "#ffffff";
  const cardTitleColor = establishment?.infoBoxTitleColor || "#0f172a";
  const cardTextColor = establishment?.infoBoxTextColor || "#475569";
  const overlayOpacity = clampThemeNumber(
    establishment?.bannerOverlayOpacity,
    0,
    0.7,
    0.2
  );
  const infoBoxOpacity = clampThemeNumber(establishment?.infoBoxOpacity, 0.35, 1, 0.92);
  const infoBoxBlur = clampThemeNumber(establishment?.infoBoxBlur, 0, 24, 10);

  return {
    baseColor,
    titleColor,
    descriptionColor,
    addressColor,
    cardBackgroundColor,
    cardTitleColor,
    cardTextColor,
    overlayOpacity,
    infoBoxOpacity,
    infoBoxBlur,
    baseSoft: hexToRgba(baseColor, 0.1) || hexToRgba(DEFAULT_BRAND_HEX, 0.1)!,
    baseSubtle: hexToRgba(baseColor, 0.16) || hexToRgba(DEFAULT_BRAND_HEX, 0.16)!,
    baseMedium: hexToRgba(baseColor, 0.72) || hexToRgba(DEFAULT_BRAND_HEX, 0.72)!,
    baseStrong: hexToRgba(baseColor, 0.92) || hexToRgba(DEFAULT_BRAND_HEX, 0.92)!,
  };
}
